import { useTranslation } from "../i18n";
import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
  Filler,
  Title,
} from 'chart.js';
import './styles/ElevationProfile.css';
import API_BASE_URL from '../config/api';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Tooltip, Legend, Filler, Title);

const GAP_DIVISOR = { ITRA: 100, UTMB: 80, IAAF: 120 };
const GAP_COLOR = { ITRA: '249,115,22', UTMB: '0,128,255', IAAF: '128,0,128' };

// Suaviza una serie (promedio móvil) para que las líneas se vean limpias, igual al mockup
const movingAverage = (arr, windowSize = 7) => {
  return arr.map((_, i) => {
    const start = Math.max(0, i - Math.floor(windowSize / 2));
    const end = Math.min(arr.length, i + Math.ceil(windowSize / 2));
    const slice = arr.slice(start, end).filter(v => v != null && !isNaN(v));
    if (!slice.length) return arr[i];
    return slice.reduce((a, b) => a + b, 0) / slice.length;
  });
};


const paceTickFormat = (value) => {
  const min = Math.floor(value);
  const sec = Math.round((value - min) * 60);
  return `${String(min).padStart(2, '0')}:${String(sec).padStart(2, '0')}`;
};

const CHART_OPTIONS = {
  responsive: true,
  maintainAspectRatio: false,
  interaction: { mode: 'index', intersect: false },
  plugins: {
    legend: {
      position: 'top',
      labels: { font: { size: 10 }, boxWidth: 12 },
    },
    tooltip: { mode: 'index', intersect: false },
  },
  scales: {
    x: {
      type: 'linear',
      title: { display: true, text: 'Distancia (km)', font: { size: 9 } },
      ticks: { font: { size: 9 }, maxTicksLimit: 10 },
    },
    elev: {
      position: 'left',
      title: { display: true, text: 'Elevación (m)', font: { size: 9 } },
      ticks: { font: { size: 9 } },
    },
    fc: {
      position: 'right',
      title: { display: true, text: 'bpm', font: { size: 9 } },
      ticks: { font: { size: 9 } },
      grid: { drawOnChartArea: false },
    },
    pace: {
      position: 'right',
      min: 4,
      max: 14,
      title: { display: true, text: 'min/km', font: { size: 9 } },
      ticks: { font: { size: 9 }, callback: paceTickFormat },
      grid: { drawOnChartArea: false },
    },
  },
};

const ElevationProfile = ({ fileId }) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [chartData, setChartData] = useState([]);
  const [summary, setSummary] = useState({});
  const [gapFormula, setGapFormula] = useState('ITRA');

  const calculateElevationGain = (elevations) => {
    let gain = 0;
    for (let i = 1; i < elevations.length; i++) {
      if (elevations[i] > elevations[i - 1]) {
        gain += elevations[i] - elevations[i - 1];
      }
    }
    return gain.toFixed(2);
  };

  const calculateNormalizedPower = (wattsArray) => {
    if (!wattsArray?.length) return null;
    const rollingAverages = [];
    for (let i = 0; i <= wattsArray.length - 30; i++) {
      const slice = wattsArray.slice(i, i + 30);
      const avg = slice.reduce((sum, w) => sum + w, 0) / slice.length;
      rollingAverages.push(avg);
    }
    const fourthPowerAvg = rollingAverages.reduce((sum, r) => sum + Math.pow(r, 4), 0) / rollingAverages.length;
    return Math.round(Math.pow(fourthPowerAvg, 0.25));
  };

  useEffect(() => {
    if (!fileId) return;

    const fetchData = async () => {
      setLoading(true);
      setError(null);

      try {
        const res = await axios.get(`${API_BASE_URL}/api/fit/parsed-data/${fileId}`);
        const data = res.data;

        if (data?.session && data?.records) {
          const thresholdHR = data.zones_target?.threshold_heart_rate ?? 170;
          const weight = data.fitFileDetails?.user_profile?.weight ?? 70;
          const records = data.records.filter(
            r => typeof r.distance === 'number' && typeof r.enhanced_altitude === 'number'
          );

          const processedData = records.map((r) => {
            const distanceKm = parseFloat((r.distance / 1000).toFixed(2));
            const pace = r.enhanced_speed > 0
              ? parseFloat(((1000 / r.enhanced_speed) / 60).toFixed(2))
              : null;
            const IF = r.heart_rate && thresholdHR
              ? parseFloat((r.heart_rate / thresholdHR).toFixed(2))
              : null;
            const watts = r.enhanced_speed
              ? parseFloat((weight * r.enhanced_speed * 0.1).toFixed(2))
              : null;

            return {
              distance: distanceKm,
              elevation: parseFloat(r.enhanced_altitude.toFixed(2)),
              heartRate: r.heart_rate,
              pace,
              IF,
              watts,
            };
          });

          setChartData(processedData);

          const elevations = processedData.map(d => d.elevation);
          const avgIF = (
            processedData.reduce((sum, d) => sum + (d.IF || 0), 0) /
            processedData.filter(d => d.IF).length
          ).toFixed(2);

          setSummary({
            totalDistance: (data.session.total_distance / 1000).toFixed(2),
            maxElevation: Math.max(...elevations).toFixed(2),
            minElevation: Math.min(...elevations).toFixed(2),
            elevationGain: calculateElevationGain(elevations),
            normalizedPower: calculateNormalizedPower(
              processedData.map(d => d.watts).filter(Boolean)
            ),
            intensityFactor: avgIF,
          });
        } else {
          throw new Error('No se encontraron datos en el archivo.');
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [fileId]);

  // GAP punto a punto: el ascenso entre puntos consecutivos penaliza el ritmo
  const chartDataWithGap = useMemo(() => {
    const divisor = GAP_DIVISOR[gapFormula] || 100;
    let prevElevation = chartData.length ? chartData[0].elevation : 0;
    return chartData.map((d) => {
      const deltaElevation = Math.max(0, d.elevation - prevElevation);
      prevElevation = d.elevation;
      const gapPace = d.pace != null ? d.pace + (deltaElevation / divisor) / 60 : null;
      return { ...d, gapPace };
    });
  }, [chartData, gapFormula]);

  const chartJsData = useMemo(() => {
    const xStart = chartDataWithGap[0]?.distance ?? 0;
    const xEnd = chartDataWithGap[chartDataWithGap.length - 1]?.distance ?? 0;
    const maxElev = parseFloat(summary.maxElevation);
    const minElev = parseFloat(summary.minElevation);
    const smoothElevation = movingAverage(chartDataWithGap.map(d => d.elevation));
    const smoothHeartRate = movingAverage(chartDataWithGap.map(d => d.heartRate));

    return {
      datasets: [
        {
          label: t('elevationProfile.seriesElevation'),
          data: chartDataWithGap.map((d, i) => ({ x: d.distance, y: smoothElevation[i] })),
          borderColor: 'rgba(0,128,0,0.9)',
          backgroundColor: 'rgba(0,128,0,0.12)',
          yAxisID: 'elev',
          fill: true,
          tension: 0.4,
          pointRadius: 0,
          pointHoverRadius: 4,
          borderWidth: 2.5,
          order: 1,
        },
        {
          label: `${t('elevationProfile.altMax')}: ${summary.maxElevation} m`,
          data: [{ x: xStart, y: maxElev }, { x: xEnd, y: maxElev }],
          borderColor: 'rgba(220,38,38,0.7)',
          backgroundColor: 'transparent',
          yAxisID: 'elev',
          fill: false,
          pointRadius: 0,
          pointHoverRadius: 3,
          borderWidth: 1.2,
          borderDash: [6, 3],
          order: 5,
        },
        {
          label: `${t('elevationProfile.altMin')}: ${summary.minElevation} m`,
          data: [{ x: xStart, y: minElev }, { x: xEnd, y: minElev }],
          borderColor: 'rgba(59,130,246,0.7)',
          backgroundColor: 'transparent',
          yAxisID: 'elev',
          fill: false,
          pointRadius: 0,
          pointHoverRadius: 3,
          borderWidth: 1.2,
          borderDash: [6, 3],
          order: 6,
        },
        {
          label: t('elevationProfile.seriesHr'),
          data: chartDataWithGap.map((d, i) => ({ x: d.distance, y: smoothHeartRate[i] })),
          borderColor: 'rgba(239,68,68,0.9)',
          backgroundColor: 'rgba(239,68,68,0.05)',
          yAxisID: 'fc',
          fill: false,
          tension: 0.4,
          pointRadius: 0,
          pointHoverRadius: 4,
          borderWidth: 1.5,
          order: 2,
        },
        {
          label: t('elevationProfile.seriesPaceReal'),
          data: chartDataWithGap.map(d => ({ x: d.distance, y: d.pace })),
          borderColor: 'rgba(59,130,246,0.9)',
          backgroundColor: 'transparent',
          yAxisID: 'pace',
          fill: false,
          tension: 0.4,
          pointRadius: 0,
          pointHoverRadius: 4,
          borderWidth: 1.5,
          borderDash: [4, 2],
          order: 3,
        },
        {
          label: `${t('elevationProfile.seriesGapAdjusted')} (${gapFormula})`,
          data: chartDataWithGap.map(d => ({ x: d.distance, y: d.gapPace })),
          borderColor: `rgba(${GAP_COLOR[gapFormula] || GAP_COLOR.ITRA},0.95)`,
          backgroundColor: 'transparent',
          yAxisID: 'pace',
          fill: false,
          tension: 0.4,
          pointRadius: 0,
          pointHoverRadius: 4,
          borderWidth: 1.8,
          order: 4,
        },
      ],
    };
  }, [chartDataWithGap, gapFormula, summary]);


  const chartJsOptions = useMemo(() => {
    const xMax = chartDataWithGap.length
      ? chartDataWithGap[chartDataWithGap.length - 1].distance
      : undefined;
    return {
      ...CHART_OPTIONS,
      scales: {
        ...CHART_OPTIONS.scales,
        x: { ...CHART_OPTIONS.scales.x, max: xMax, title: { ...CHART_OPTIONS.scales.x.title, text: t('elevationProfile.axisDistance') } },
        elev: { ...CHART_OPTIONS.scales.elev, title: { ...CHART_OPTIONS.scales.elev.title, text: t('elevationProfile.axisElevation') } },
        fc: { ...CHART_OPTIONS.scales.fc, title: { ...CHART_OPTIONS.scales.fc.title, text: t('elevationProfile.axisBpm') } },
        pace: { ...CHART_OPTIONS.scales.pace, title: { ...CHART_OPTIONS.scales.pace.title, text: t('elevationProfile.axisPaceMinKm') } },
      },
    };
  }, [chartDataWithGap, t]);

  if (!fileId) return <div>⚠️ No se recibió fileId</div>;
  if (loading) return <div>{t("elevationProfile.loading")}</div>;
  if (error) return <div>{t('social.elevationProfile.loading')} {error}</div>;
  if (!chartData.length) return null;

  return (
    <div style={{ width: '100%' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4, flexWrap: 'wrap', gap: 8 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: '#193255' }}>
          {t('elevationProfile.title')}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <label style={{ fontSize: 11, color: '#64748b', margin: 0 }}>
            Ritmo ajustado (GAP):
          </label>
          <select
            style={{ fontSize: 11 }}
            value={gapFormula}
            onChange={(e) => setGapFormula(e.target.value)}
          >
            <option value="ITRA">ITRA</option>
            <option value="UTMB">UTMB</option>
            <option value="IAAF">IAAF</option>
          </select>
        </div>
      </div>
      <div style={{ fontSize: 11, color: '#64748b', marginBottom: 6 }}>
        {t('elevationProfile.totalDistance')} {summary.totalDistance} km · {t('elevationProfile.maxElevation')} {summary.maxElevation} m · {t('elevationProfile.minElevation')} {summary.minElevation} m · {t('elevationProfile.positiveSlope')} {summary.elevationGain} m
      </div>
      <div style={{ width: '100%', height: '260px' }}>
        <Line data={chartJsData} options={chartJsOptions} />
      </div>
    </div>
  );
};

export default ElevationProfile;
